"use client";

import { getPropertySlug } from "@repo/core/client";
import { Button } from "@ui/components/button";
import {
	CopyIcon,
	FileTextIcon,
	LockIcon,
	MailIcon,
	PresentationIcon,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { type ReactNode, useState } from "react";

export interface ShareButtonsExportLabels {
	exportSection: string;
	unlockToExportPdf: string;
	unlockToExportPpt: string;
	exportPdf: string;
	exportPpt: string;
	exporting: string;
	copied: string;
}

export interface ShareButtonsExportConfig {
	reportId: string;
	isUnlocked: boolean;
	reportPublicToken: string;
	/** Null/empty for PERSON and CATEGORY subjects — pair it with `companyName`. */
	domain?: string | null;
	/** Preferred over `domain` when naming the exported file. */
	companyName?: string | null;
	labels: ShareButtonsExportLabels;
	/** Custom endpoints for triggering and polling export status. Defaults to AI reports endpoints. */
	exportEndpoint?: string;
	exportStatusEndpoint?: string;
	/**
	 * Set to false for a report that has no deck (a category). The PowerPoint
	 * button is then left out, locked or not. Defaults to true.
	 */
	allowPpt?: boolean;
}

export interface ShareButtonsProps {
	/** Stack buttons vertically (e.g. in a side panel) */
	vertical?: boolean;
	/** Title used for share intent (e.g. "AI Readiness Audit for example.com") */
	shareTitle: string;
	/** UTM campaign name for attribution */
	utmCampaign: string;
	/** Optional export section (PDF/PPT for AI visibility reports) */
	exportConfig?: ShareButtonsExportConfig;
	/**
	 * Optional "Chat with this report" actions (the report AI deep-links +
	 * Copy/View Markdown). Rendered as the first section. The node is supplied by
	 * the caller (e.g. <ReportAIActions />) so this UI component stays free of
	 * marketing/report dependencies.
	 */
	aiActions?: ReactNode;
	/** Heading for the aiActions section (default: "Chat with this report") */
	aiActionsHeading?: string;
	/** Optional labels for Copy / Email (default: "Copied!", "Copy Link", "Email") */
	copyLabel?: string;
	copiedLabel?: string;
	emailLabel?: string;
	/** Section headings - optional overrides */
	socialHeading?: string;
	privateHeading?: string;
}

export function ShareButtons({
	vertical = false,
	shareTitle,
	utmCampaign,
	exportConfig,
	aiActions,
	aiActionsHeading = "Chat with this report",
	copyLabel = "Copy Link",
	copiedLabel = "Copied!",
	emailLabel = "Email",
	socialHeading = "Social Media",
	privateHeading = "Private Communities",
}: ShareButtonsProps) {
	const [copied, setCopied] = useState(false);
	const [copiedPlatform, setCopiedPlatform] = useState<string | null>(null);
	const [exportingPdf, setExportingPdf] = useState(false);
	const [exportingPpt, setExportingPpt] = useState(false);

	const handleCopyLink = async () => {
		try {
			const urlWithUtm = addUtmParameters(window.location.href, "copy");
			await navigator.clipboard.writeText(urlWithUtm);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error("Failed to copy link:", err);
		}
	};

	const handleCopyForPlatform = async (platform: string) => {
		try {
			const urlWithUtm = addUtmParameters(window.location.href, platform);
			await navigator.clipboard.writeText(urlWithUtm);
			setCopiedPlatform(platform);
			setTimeout(() => setCopiedPlatform(null), 2000);
		} catch (err) {
			console.error(`Failed to copy link for ${platform}:`, err);
		}
	};

	const addUtmParameters = (baseUrl: string, source: string) => {
		const url = new URL(baseUrl);
		url.searchParams.set("utm_source", source);
		url.searchParams.set("utm_medium", "social");
		url.searchParams.set("utm_campaign", utmCampaign);
		return url.toString();
	};

	const handleShare = (platform: string) => {
		if (platform === "slack" || platform === "discord") {
			handleCopyForPlatform(platform);
			return;
		}

		const urlWithUtm = addUtmParameters(window.location.href, platform);
		const url = encodeURIComponent(urlWithUtm);
		const title = encodeURIComponent(shareTitle);

		const shareUrls: Record<string, string> = {
			teams: `https://teams.microsoft.com/share?text=${title}&url=${url}`,
			linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
			reddit: `https://www.reddit.com/submit?url=${url}&title=${title}`,
			x: `https://twitter.com/intent/tweet?text=${title}&url=${url}`,
			threads: `https://www.threads.net/intent/post?text=${title} ${urlWithUtm}`,
			email: `mailto:?subject=${title}&body=Check out this report: ${urlWithUtm}`,
		};

		window.open(shareUrls[platform] ?? "#", "_blank");
	};

	const handleExport = async (format: "pdf" | "pptx") => {
		if (!exportConfig) return;
		const setLoading = format === "pdf" ? setExportingPdf : setExportingPpt;
		setLoading(true);

		try {
			// The AI-report export endpoints authorize either a session whose
			// user belongs to the report's organization, or the report's own
			// public token. This component renders on the shareable report page,
			// which has no session, so it sends the token. Callers that override
			// the endpoints (the public site-audit report) already carry their
			// token in the path, so nothing is appended for them.
			const shareToken = exportConfig.reportPublicToken;
			const tokenQuery = shareToken
				? `token=${encodeURIComponent(shareToken)}`
				: "";

			const triggerUrl = exportConfig.exportEndpoint
				? exportConfig.exportEndpoint
				: `/api/ai-reports/export/${exportConfig.reportId}${
						tokenQuery ? `?${tokenQuery}` : ""
					}`;
			const statusUrl =
				exportConfig.exportStatusEndpoint ||
				`/api/ai-reports/${exportConfig.reportId}/export-status/`;
			const statusQuery =
				!exportConfig.exportStatusEndpoint && tokenQuery
					? `&${tokenQuery}`
					: "";

			const response = await fetch(triggerUrl, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ format }),
			});

			if (!response.ok) {
				const error = await response.json();
				alert(`Failed to export: ${error.error || "Unknown error"}`);
				setLoading(false);
				return;
			}

			const maxAttempts = 60;
			let attempts = 0;

			while (attempts < maxAttempts) {
				await new Promise((r) => setTimeout(r, 1000));
				const statusResponse = await fetch(
					`${statusUrl}?format=${format}${statusQuery}`,
				);
				if (!statusResponse.ok) break;
				const status = await statusResponse.json();

				if (status.ready && status.url) {
					if (format === "pdf") {
						window.open(status.url, "_blank");
					} else {
						const link = document.createElement("a");
						link.href = status.url;
						link.download = `ai-visibility-report-${getPropertySlug(exportConfig)}.${format}`;
						document.body.appendChild(link);
						link.click();
						document.body.removeChild(link);
					}
					break;
				}
				attempts++;
			}

			if (attempts >= maxAttempts) {
				alert(
					"Export is taking longer than expected. Please try again later.",
				);
			}
		} catch (error) {
			console.error("Export error:", error);
			alert("Failed to export report. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const btn = (className: string) =>
		vertical ? `${className} w-full justify-start` : className;

	return (
		<div className="space-y-6">
			{aiActions && (
				<>
					<div>
						<h4 className="text-sm font-medium text-gray-900 mb-3">
							{aiActionsHeading}
						</h4>
						{aiActions}
					</div>
					<div className="border-t border-gray-200" />
				</>
			)}

			{exportConfig && (
				<>
					<div>
						<h4 className="text-sm font-medium text-gray-900 mb-3">
							{exportConfig.labels.exportSection}
						</h4>
						<div
							className={
								vertical ? "space-y-2" : "flex gap-2 flex-wrap"
							}
						>
							{!exportConfig.isUnlocked ? (
								<>
									<Link
										href={`/reports/${exportConfig.reportPublicToken}/unlock`}
										className={`inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-sm font-medium px-3 py-2 rounded-md border border-transparent transition-colors ${vertical ? "w-full" : ""}`}
										data-testid="export-pdf-locked"
									>
										<LockIcon className="w-4 h-4 flex-shrink-0" />
										<span className="truncate">
											{
												exportConfig.labels
													.unlockToExportPdf
											}
										</span>
									</Link>
									{exportConfig.allowPpt === false ? null : (
										<Link
											href={`/reports/${exportConfig.reportPublicToken}/unlock`}
											className={`inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-sm font-medium px-3 py-2 rounded-md border border-transparent transition-colors ${vertical ? "w-full" : ""}`}
											data-testid="export-ppt-locked"
										>
											<LockIcon className="w-4 h-4 flex-shrink-0" />
											<span className="truncate">
												{
													exportConfig.labels
														.unlockToExportPpt
												}
											</span>
										</Link>
									)}
								</>
							) : (
								<>
									<Button
										variant="outline"
										className={btn("")}
										onClick={() => handleExport("pdf")}
										disabled={exportingPdf}
										data-testid="export-pdf"
									>
										<div className="flex items-center gap-2">
											<FileTextIcon className="w-4 h-4 text-red-600 flex-shrink-0" />
											<span className="truncate">
												{exportingPdf
													? exportConfig.labels
															.exporting
													: exportConfig.labels
															.exportPdf}
											</span>
										</div>
									</Button>
									{exportConfig.allowPpt === false ? null : (
										<Button
											variant="outline"
											className={btn("")}
											onClick={() => handleExport("pptx")}
											disabled={exportingPpt}
											data-testid="export-ppt"
										>
											<div className="flex items-center gap-2">
												<PresentationIcon className="w-4 h-4 text-orange-600 flex-shrink-0" />
												<span className="truncate">
													{exportingPpt
														? exportConfig.labels
																.exporting
														: exportConfig.labels
																.exportPpt}
												</span>
											</div>
										</Button>
									)}
								</>
							)}
						</div>
					</div>
					<div className="border-t border-gray-200" />
				</>
			)}

			<div>
				<h4 className="text-sm font-medium text-gray-900 mb-3">
					{socialHeading}
				</h4>
				<div
					className={vertical ? "space-y-2" : "flex gap-2 flex-wrap"}
				>
					<Button
						variant="outline"
						className={btn("")}
						onClick={() => handleShare("linkedin")}
						data-testid="share-linkedin"
					>
						<div className="flex items-center space-x-3">
							<Image
								src="/images/linkedin-logo.svg"
								alt="LinkedIn"
								width={20}
								height={20}
								className="w-5 h-5"
							/>
							<span>LinkedIn</span>
						</div>
					</Button>
					<Button
						variant="outline"
						className={btn("")}
						onClick={() => handleShare("reddit")}
						data-testid="share-reddit"
					>
						<div className="flex items-center space-x-3">
							<Image
								src="/images/reddit-logo.svg"
								alt="Reddit"
								width={20}
								height={20}
								className="w-5 h-5"
							/>
							<span>Reddit</span>
						</div>
					</Button>
					<Button
						variant="outline"
						className={btn("")}
						onClick={() => handleShare("threads")}
						data-testid="share-threads"
					>
						<div className="flex items-center space-x-3">
							<Image
								src="/images/threads-logo.svg"
								alt="Threads"
								width={20}
								height={20}
								className="w-5 h-5"
							/>
							<span>Threads</span>
						</div>
					</Button>
					<Button
						variant="outline"
						className={btn("")}
						onClick={() => handleShare("x")}
						data-testid="share-x"
					>
						<div className="flex items-center space-x-3">
							<Image
								src="/images/twitter-x-logo.svg"
								alt="Twitter (X)"
								width={20}
								height={20}
								className="w-5 h-5"
							/>
							<span>Twitter (X)</span>
						</div>
					</Button>
				</div>
			</div>

			<div className="border-t border-gray-200" />

			<div>
				<h4 className="text-sm font-medium text-gray-900 mb-3">
					{privateHeading}
				</h4>
				<div
					className={vertical ? "space-y-2" : "flex gap-2 flex-wrap"}
				>
					<Button
						variant="outline"
						className={btn("")}
						onClick={() => handleShare("slack")}
						data-testid="share-slack"
					>
						<div className="flex items-center space-x-3">
							<Image
								src="/images/slack-logo.svg"
								alt="Slack"
								width={20}
								height={20}
								className="w-5 h-5"
							/>
							<span>
								{copiedPlatform === "slack"
									? (exportConfig?.labels.copied ??
										copiedLabel)
									: "Slack"}
							</span>
						</div>
					</Button>
					<Button
						variant="outline"
						className={btn("")}
						onClick={() => handleShare("teams")}
						data-testid="share-teams"
					>
						<div className="flex items-center space-x-3">
							<Image
								src="/images/microsoft-teams-logo.svg"
								alt="Microsoft Teams"
								width={20}
								height={20}
								className="w-5 h-5"
							/>
							<span>Microsoft Teams</span>
						</div>
					</Button>
					<Button
						variant="outline"
						className={btn("")}
						onClick={() => handleShare("discord")}
						data-testid="share-discord"
					>
						<div className="flex items-center space-x-3">
							<Image
								src="/images/discord-icon.svg"
								alt="Discord"
								width={20}
								height={20}
								className="w-5 h-5"
							/>
							<span>
								{copiedPlatform === "discord"
									? (exportConfig?.labels.copied ??
										copiedLabel)
									: "Discord"}
							</span>
						</div>
					</Button>
				</div>
			</div>

			<div className="border-t border-gray-200" />

			<div>
				<div
					className={vertical ? "space-y-2" : "flex gap-2 flex-wrap"}
				>
					<Button
						variant="secondary"
						className={vertical ? "w-full" : ""}
						onClick={handleCopyLink}
						data-testid="copy-link"
					>
						<CopyIcon className="w-4 h-4 mr-2" />
						{copied ? copiedLabel : copyLabel}
					</Button>
					<Button
						variant="outline"
						className={vertical ? "w-full" : ""}
						onClick={() => handleShare("email")}
						data-testid="share-email"
					>
						<MailIcon className="w-4 h-4 mr-2" />
						{emailLabel}
					</Button>
				</div>
			</div>
		</div>
	);
}
